import { useThreadContext } from "../page/threadPageContext";
import { approvalName } from "./toolApproval";
import { useTranslation } from "react-i18next";
import CopyReference from "../../../ui/content/CopyReference";
import StatusMessage from "../../../ui/feedback/StatusMessage";
import { shortReference } from "../../../shared/references/shortReferences";
import type { ToolApproval } from "./toolApproval";
import ToolApprovalAnswer from "./ToolApprovalAnswer";
import { useApprovalTarget } from "./useApprovalPage";
import OpenOnPc from "../../connectors/pcPage/OpenOnPc";
import ToolAbout from "./ToolAbout";

interface ToolApprovalNoticeProps {
  approval: ToolApproval;
  /** The step waits for the approval and runs once it is given (TR-B1-01); `false` for a run that failed before. */
  waiting: boolean;
  /** The Thread's connector, when the viewer hosts it: they may answer here, or on its page. */
  hostConnectorId: string | null;
}

/** One command to type on the host's PC, with a button that copies it. */
function Command({ text }: { text: string }) {
  const { t } = useTranslation();
  return (
    <div className="app-code-panel mt-1 flex items-center gap-2 px-2 py-1">
      <code className="min-w-0 flex-1 break-all text-xs">{text}</code>
      <CopyReference value={text} label={t("threads.toolApproval.copyCommand")} />
    </div>
  );
}

/**
 * A step whose tool version waits for the PC owner's approval (web-app.md WA-3). It says on
 * which PC it runs, plainly when that is not this one, and that it runs by itself once approved
 * (TR-B1-01). The host answers right here, wherever they are signed in (TA-6; Johannes, 3 Nov
 * 18:47); the PC's own page stays a second place, opened there through the server (F85), and
 * the first answer wins. The terminal is the folded backup.
 */
export default function ToolApprovalNotice({ approval, waiting, hostConnectorId }: ToolApprovalNoticeProps) {
  const { t } = useTranslation();
  const { stepName } = useThreadContext();
  const target = useApprovalTarget(hostConnectorId);
  const tool = approvalName(approval, stepName, t("threads.toolApproval.agentsTool"));
  const pc = target?.pc ?? t("threads.toolApproval.hostPc");
  const answerable = waiting && target !== null ? approval.answerable : null;
  return (
    <div role="alert">
      <StatusMessage tone="warning" title={answerable === null ? t("threads.toolApproval.title", { tool }) : t("threads.toolApproval.wantsToRun", { tool, pc })}>
        {target?.elsewhere === true ? <p className="font-semibold">{t("threads.toolApproval.notThisPc", { pc })}</p> : null}
        {answerable !== null ? (
          <>
            <ToolAbout tool={approval.toolReference} />
            <p>{t("threads.toolApproval.answerHere", { pc })}</p>
            <ToolApprovalAnswer request={answerable} pc={pc} />
          </>
        ) : (
          <>
            {waiting ? <p>{t("threads.toolApproval.waiting", { pc })}</p> : null}
            <p>{t("threads.toolApproval.message")}</p>
          </>
        )}
        {target === null || !target.pageServed ? null : (
          <div className="mt-3">
            <OpenOnPc
              connectorId={target.connectorId}
              pc={target.pc}
              section="approvals"
              label={t("threads.toolApproval.openApprovalsOn", { pc: target.pc })}
              command={{ args: "tools page", aboutServer: true }}
              variant={answerable === null ? "primary" : "secondary"}
            />
          </div>
        )}
        <details className="mt-3 text-xs">
          <summary className="cursor-pointer">{t("threads.toolApproval.useCommand")}</summary>
          <p className="mt-2 flex flex-wrap items-center gap-1">
            {t("threads.toolApproval.version", { tool: approval.toolName, version: shortReference("version", approval.toolReference) })}
            <CopyReference value={approval.toolReference} label={t("threads.toolApproval.copyVersion")} />
          </p>
          <p className="mt-2">{t("threads.toolApproval.inTerminal")}</p>
          <Command text={approval.connectorCommand} />
          <p className="mt-2">{t("threads.toolApproval.answer")}</p>
        </details>
      </StatusMessage>
    </div>
  );
}
